import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableBrandFilterApplied,
  fetchAllBrands,
  fetchBrandFilters,
  setBrandLimit,
  setBrandPage,
  setBrandSortBy,
  setBrandSortField,
  updateBrandFilters,
} from '../../../redux/slices/BrandSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddBrandDrawer from '../../ev-device-hub/components/drawers/AddBrandDrawer';
import EditBrandDrawer from '../../ev-device-hub/components/drawers/EditBrandDrawer';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Local brand management component — displays brands belonging to the current tenant.
 * @returns {React.JSX.Element} Local brand management component
 */
function Local(): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('deviceHub');
  const {
    brands,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.brand);

  const [brandDrawerOpen, setBrandDrawerOpen] = useState(false);
  const [editBrandOpen, setEditBrandOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllBrands({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllBrands({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableBrandFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setBrandSortBy(sortData.sortBy));
    dispatch(setBrandSortField(sortData.sortField));
    dispatch(
      fetchAllBrands({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setBrandPage(value.page));
    if (value.limit !== undefined) dispatch(setBrandLimit(value.limit));
    dispatch(
      fetchAllBrands({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes brand data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllBrands({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Calls the API to delete a brand
   * @param {string} brandId - The ID of the brand to delete
   */
  const handleConfirmDeleteBrand = (brandId: string) => {
    deviceApi
      .deleteBrand(brandId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('brandDeletedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message ||  t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Triggers the delete confirmation warning modal for a brand
   * @param {string} brandId - The ID of the brand
   */
  const handleDeleteBrandClick = (brandId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={brandId}
          title={t('deleteBrandConfirmation')}
          description={t('deleteBrandConfirmationDesc')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteBrand(id)}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    {
      label: t('brandName'),
      accessorKey: 'name',
    },
    {
      label: t('companyName'),
      accessorKey: 'companyName',
    },
    {
      label: t('country'),
      accessorKey: 'country',
    },
    {
      label: t('contactEmail'),
      accessorKey: 'email',
      /**
       * Renders contact email
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.email || t('notAvailable')}</span>,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'phoneNumber',
      /**
       * Renders phone number
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>
          {currentRow.phoneNumber
            ? `${currentRow.countryCode || ''} ${currentRow.phoneNumber}`
            : t('notAvailable')}
        </span>
      ),
    },
    {
      label: t('deviceCount'),
      accessorKey: 'deviceCount',
      sortAllow: true,
      /**
       * Renders device count
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.deviceCount || 0}</span>,
    },
    {
      label: t('actions'),
      accessorKey: 'actions',
      sortAllow: false,
      columnWidth: 80,
      /**
       * Renders row action buttons
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          {permissionCheck('devicesBrandsAdd') && (
            <>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedBrand(currentRow);
                  setEditBrandOpen(true);
                }}>
                <EditIcon className="icon edit" />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteBrandClick(currentRow._id);
                }}>
                <DeleteIcon className="icon delete" />
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo', 'S.No'), accessorKey: '__serial__' },
    { label: t('brandName'), accessorKey: 'name' },
    { label: t('companyName'), accessorKey: 'companyName' },
    { label: t('country'), accessorKey: 'country' },
    { label: t('contactEmail'), accessorKey: 'email' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumber' },
    { label: t('deviceCount'), accessorKey: 'deviceCount' },
    { label: t('createdAt', 'Created At'), accessorKey: 'createdAt' },
    { label: t('updatedAt', 'Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export brands data in PDF or CSV format
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    deviceApi
      .exportBrands(removeEmptyKeys(payload))
      .then((response: any) => {
        const rawList = response?.data?.results || response?.data || [];
        const exportDataList = Array.isArray(rawList) ? rawList : [];

        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = exportDataList.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);

              case 'name':
                return row?.name || '-';

              case 'companyName':
                return row?.companyName || '-';

              case 'country':
                return row?.country || '-';

              case 'email':
                return row?.email || '-';

              case 'phoneNumber':
                return row?.phoneNumber
                  ? `${row.countryCode ? `${row.countryCode} ` : ''}${row.phoneNumber}`
                  : '-';

              case 'deviceCount':
                return row?.deviceCount !== undefined && row?.deviceCount !== null
                  ? String(row.deviceCount)
                  : '0';

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDateTime(row.createdAt) : '-';

              case 'updatedAt':
                return row?.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : '-';

              default:
                return row?.[col.accessorKey] ?? '-';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'brands-list.csv');
          } else {
            exportPDF(headers, rows, 'brands-list.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <CustomTable
        dataKey="BrandManagement"
        header={column}
        data={brands}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={310}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateBrandFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchBrandFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        refershCurrentPages={handleRefresh}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        onHiddenColumnsChange={setHiddenColumns}
        rightTopContent={
          <div className="table-bottom-button-list">
            {permissionCheck('devicesBrandsAdd') && (
              <div className="table-button-item">
                <MyButton
                  label={t('addBrand')}
                  buttonType="submit"
                  onClickFunc={() => setBrandDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
      />

      <AddBrandDrawer
        open={brandDrawerOpen}
        onClose={() => setBrandDrawerOpen(false)}
        onBrandCreated={() => {
          setBrandDrawerOpen(false);
          handleRefresh();
        }}
      />

      <EditBrandDrawer
        open={editBrandOpen}
        brandData={selectedBrand}
        onClose={() => {
          setEditBrandOpen(false);
          setSelectedBrand(null);
        }}
        onBrandUpdated={() => {
          setEditBrandOpen(false);
          setSelectedBrand(null);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
